import {
  useState,
  useRef,
  useEffect,
  useMemo,
  forwardRef,
  cloneElement,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import { defaultsDeep } from 'lodash';
import DateTextInlineInput from 'components/Inputs/inline/TextInput/presets/DatePicker';
import DateInput, {
  Menu,
  getDateOption,
  DATE_EMPTY_FORMAT,
} from '../DateInput';
import { basePopperConfig, baseModalPopperConfig } from './helpers';
import { TextInputInlineWrapper } from './TextInput/styles';
import SelectOverlay from '../SelectOverlay';
import { popperModifiers } from 'components/helpers';
import { WithTooltip } from './WithTooltip';
import { FIELD_TYPES } from 'utility/constants';
import { useKeyBoardContext } from '../../../hooks/keyboardEventHandler/useKeyBoardContext';
import { useCustomSelectMenuProps } from '../../../hooks/keyboardEventHandler/useCustomSelectMenuProps';
import { getAdornments } from 'components/Inputs/Adornments/util';
import { toastVariant, useToast } from 'components/ToastProvider';
import { useRecordDetailCustomScript } from '@kizenapps/engine/react';
import { WithIconDataAdornments } from '../Adornments/WithIconDataAdornments';
import { ADORNMENT_VARIANTS } from '../Adornments/constants';

export const DateInputInlineSelect = forwardRef(
  ({ onChange = null, ...others }, ref) => {
    return (
      <DateInput
        ref={ref}
        menuOnly
        onChange={(val) => {
          // date -> option for compatibility with SelectInline
          return onChange?.(getDateOption(val));
        }}
        {...others}
      />
    );
  }
);

DateInputInlineSelect.displayName = 'DateInputInlineSelect';

export const nudgePopperUp = defaultsDeep({}, basePopperConfig, {
  modifiers: [
    popperModifiers.offSet(0, -2), // Align directly under TextControl text
  ],
});

// baseModalPopperConfig sets the zIndex to zIndexes.popoverInModal
export const modalNudgePopperUp = defaultsDeep({}, baseModalPopperConfig, {
  modifiers: [
    popperModifiers.offSet(0, -9), // Align directly under EditableText text
  ],
});

export default function DateInputInline({
  onSubmit = null,
  children: selectEl = <DateInputInlineSelect />,
  inModal = false,
  readOnly = false,
  autoFocus = null,
  showToolTip = false,
  tooltipLabel = '',
  popperConfig = null,
  placement = 'top',
  labelEl = null,
  className = '',
  trackExternal = true,
  placeholder = '',
  field,
  objectId,
  shouldFocusNext,
  entityId,
  activityId,
  adornmentEntityId,
  adornmentObjectId,
  keyListenerId,
  adornments = [],
  compactAlignment = false,
  rootCloseEvent,
  onSetShow,
  ...others
}) {
  const { value: valueProp } = selectEl.props;
  const dateRef = useRef();
  const valuePropRef = useRef(valueProp);
  const [submitting, setSubmitting] = useState(false);
  const [showDate, _setShowDate] = useState(false);
  const [showToast] = useToast();
  const { t } = useTranslation();
  const setShowDate = useCallback(
    (val) => {
      if (typeof val === 'function') {
        _setShowDate((prev) => {
          const next = val(prev);
          onSetShow?.(next);
          return next;
        });
      } else {
        _setShowDate(val);
        onSetShow?.(val);
      }
    },
    [onSetShow]
  );

  const [date, setDate] = useState(() => getDateOption(valueProp));

  // need to watch value prop incase it get's updated externally
  if (trackExternal && valuePropRef.current !== valueProp) {
    setDate(getDateOption(valueProp));
    valuePropRef.current = valueProp;
  }

  const handleSubmit = async (v) => {
    try {
      setSubmitting(true);
      const newValue = v ? v.date : date.date;
      setShowDate(false);
      await onSubmit(newValue);
      if (v) setDate(v);
      return true;
    } catch (err) {
      setDate(getDateOption(valueProp));
      return false;
    } finally {
      setSubmitting(false);
    }
  };
  const { assignFieldHandle, getKeyListenersProps } = useKeyBoardContext();
  const handleHide = () => {
    setShowDate(false);
    if (onSubmit) {
      handleSubmit();
    }
    if (assignFieldHandle && !shouldFocusNext) {
      dateRef.current?.querySelector('input')?.focus();
    }
  };
  useEffect(() => {
    if (autoFocus && !assignFieldHandle) {
      setShowDate(true);
    }
  }, [autoFocus, assignFieldHandle, setShowDate]);

  const fieldId = useMemo(
    () => keyListenerId || `${field?.id}-${entityId}`,
    [field, entityId, keyListenerId]
  );

  const { setMenuIsOpen, ...customSelectMenuProps } = useCustomSelectMenuProps(
    selectEl,
    {
      shouldUseKeyboardEventHandle: true,
      onBlur: (e, shouldGoNext) =>
        getKeyListenersProps?.(fieldId)?.onBlur(e, shouldGoNext),
      ...others,
    }
  );
  assignFieldHandle(fieldId, {
    customUp: () => {
      dateRef.current?.querySelector('input')?.focus({ preventScroll: true });
      setShowDate(true);
    },
    customDown: () => {
      dateRef.current?.querySelector('input')?.focus({ preventScroll: true });
      setShowDate(true);
    },
    disabled: readOnly,
    shouldFocusNext,
  });

  const onClickInput = () => {
    if (!showDate && !readOnly) {
      setShowDate(true);
    }
  };

  const onHideOverlay = (e) => {
    if (e && !dateRef.current.contains(e.target)) {
      handleHide();
    }
  };

  const onSubmitInput = (val) => {
    handleSubmit(getDateOption(val));
  };

  const onParentControl = () => {
    setShowDate(false);
  };

  const [executeScript, internalState] = useRecordDetailCustomScript({
    entityId,
    objectId,
    onError: (e) => {
      showToast({
        message: `${t('Action could not run')}: ${e?.message}`,
        variant: toastVariant.FAILURE,
      });
    },
  });

  const dateAdornments = getAdornments(adornments, field);
  const hasAdornments = Boolean(
    dateAdornments.length > 0 && (date.date || date.value)
  );

  return (
    <>
      <WithIconDataAdornments
        hasAdornments={hasAdornments}
        adornments={dateAdornments}
        scriptArgs={{
          value: date,
          fieldId: field?.id,
          fieldType: field?.fieldType,
          objectId: adornmentObjectId ?? objectId,
          entityId: adornmentEntityId ?? entityId,
          activityId,
        }}
        disabled={internalState.pending}
        executeScript={executeScript}
        variant={ADORNMENT_VARIANTS.inline}
        wrapperTranslateYValue={readOnly ? '-3px' : '-1px'}
        translateYValue={readOnly ? '3px' : '1px'}
      >
        <TextInputInlineWrapper ref={dateRef} button={false}>
          <WithTooltip
            tooltipLabel={tooltipLabel}
            popperConfig={popperConfig}
            placement={placement}
            showToolTip={showToolTip}
          >
            <DateTextInlineInput
              field={field}
              entityId={entityId}
              className={`${className} ViewText DateText`}
              disabled={submitting}
              onSubmit={onSubmitInput}
              isDateInput
              readOnly={readOnly}
              isDateTextInlineInput
              showToolTip={showToolTip}
              showError
              emptyPlaceholder={labelEl ? DATE_EMPTY_FORMAT : '—'}
              onClick={onClickInput}
              {...others}
              value={date ? date.label : ''}
              placeholder={placeholder || FIELD_TYPES.Date.placeholder}
              {...getKeyListenersProps?.(fieldId)}
              shouldFocusNext={shouldFocusNext}
              onParentControl={onParentControl}
              keyListenerId={keyListenerId}
              compactAlignment={compactAlignment}
            />
          </WithTooltip>
          <SelectOverlay
            show={showDate}
            target={dateRef.current}
            onHide={onHideOverlay}
            popperConfig={inModal ? modalNudgePopperUp : nudgePopperUp}
            iDoNotWantToMountTwice
            rootCloseEvent={rootCloseEvent}
          >
            {cloneElement(selectEl, {
              onChange: handleSubmit,
              value: date,
              sizing: false,
              components: { Menu },
              ...customSelectMenuProps,
            })}
          </SelectOverlay>
        </TextInputInlineWrapper>
      </WithIconDataAdornments>
    </>
  );
}
